import React, { useState } from 'react';
import { Container, Typography, Grid, Box, Modal, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';

const Gallery = () => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(() => {
    return i18n.language || localStorage.getItem('menuLanguage') || 'en';
  });

  // Sync language state with i18n.language when it changes (one-way: i18n -> state)
  React.useEffect(() => {
    if (i18n.language && i18n.language !== language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  // When user changes language via toggle, update localStorage and i18n
  const handleLanguageChange = React.useCallback((newLang) => {
    setLanguage(newLang);
    localStorage.setItem('menuLanguage', newLang);
    i18n.changeLanguage(newLang);
  }, [language, i18n]);

  // Example gallery images (replace with your own URLs)
  const images = [
    '/images/gallery-images/1000006923-01-scaled.jpeg',
    '/images/gallery-images/20231012_212624-scaled.jpg',
    '/images/gallery-images/20231014_1615451-scaled.jpg',
    '/images/gallery-images/20231014_162438-scaled.jpg',
    '/images/gallery-images/20231021_200422-scaled.jpg',
    '/images/gallery-images/20231101_210940-scaled.jpg',
    '/images/gallery-images/20231106_183210-scaled.jpg',
    '/images/gallery-images/20231106_183229-scaled.jpg',
    '/images/gallery-images/20231125113618_IMG_0076-scaled.jpg',
    '/images/gallery-images/20231202211434_IMG_0203-1-scaled.jpg',
    '/images/gallery-images/20231204213504_IMG_0262-scaled.jpg',
    '/images/gallery-images/20231210093606_IMG_0310-scaled.jpg',
    '/images/gallery-images/20240221_221259-scaled.jpg',
    '/images/gallery-images/396994153_1194706848958249_6821451955980784087_n-1.jpg',
    '/images/gallery-images/407919914_1225581092537491_4210383140292762734_n.jpg',
    '/images/gallery-images/IMG_20231125_120650_287.webp',
    '/images/gallery-images/IMG_20231202_210827_141.webp',
  ];

  const [open, setOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);

  const handleOpen = (img) => {
    setSelectedImg(img);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedImg(null);
  };

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    bgcolor: 'rgba(0,0,0,0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1300,
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#181818',
      pt: 12,
      pb: 8,
      px: { xs: 2, md: 8 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <LanguageSwitcher language={language} setLanguage={handleLanguageChange} style="dark" floating={true} />

      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 800,
            fontFamily: 'Yeseva One, serif',
            mb: 2,
            background: 'linear-gradient(135deg, #fff 0%, #d19a06ff 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '2rem', sm: '2.3rem', md: '2.5rem' },
            letterSpacing: { xs: 1, md: 2 },
            textShadow: '0 4px 20px rgba(237, 80, 52, 0.3)'
          }}
        >
          {t('gallery.title')}
        </Typography>
        <Box sx={{
          width: { xs: 80, md: 120 },
          height: { xs: 5, md: 6 },
          background: 'linear-gradient(90deg, transparent, #e08c0eff, transparent)',
          borderRadius: 3,
          mb: 3,
          mx: 'auto',
          boxShadow: '0 0 20px rgba(237, 80, 52, 0.5)'
        }} />
      </Box>
      <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: 1400 }}>
        {images.map((img, idx) => (
          <Grid item xs={12} sm={6} md={3} key={img}>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #232323 60%, #222 100%)',
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 2px 24px rgba(0,0,0,0.28)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                border: '2px solid #222',
                '&:hover': {
                  transform: 'scale(1.04)',
                  boxShadow: '0 8px 48px #ed5034',
                  borderColor: '#ed5034',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: { xs: 180, sm: 220, md: 240 },
              }}
              onClick={() => handleOpen(img)}
            >
              <img
                src={img}
                alt={`Gallery ${idx + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'filter 0.2s',
                  filter: 'brightness(0.92) saturate(1.1)',
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Modal open={open} onClose={handleClose} sx={{ zIndex: 1300 }}>
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(0,0,0,0.98)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300,
        }}>
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', top: 32, right: 32, color: '#fff', zIndex: 1400, bgcolor: 'rgba(0,0,0,0.4)' }}
            size="large"
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          {selectedImg && (
            <img
              src={selectedImg}
              alt="Preview"
              style={{
                maxWidth: '90vw',
                maxHeight: '80vh',
                borderRadius: 24,
                boxShadow: '0 8px 48px #ed5034',
                objectFit: 'contain',
                background: '#222',
                border: '4px solid #ed5034',
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Gallery;